/* eslint-disable no-console */
// context/NavigationContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import * as ScrollUtils from 'utils/scroll';

// Create a context that will survive HMR by storing it in a module-level variable
// This approach uses the fact that React preserves context identity across HMR updates
const contextHolder = window.__NAV_CONTEXT_HOLDER || {};
if (!contextHolder.context) {
  contextHolder.context = createContext({
    activeSection: 'hero',
    isMobileNavOpen: false,
    handleSectionChange: () => {},
    toggleMobileNav: () => {},
    closeMobileNav: () => {},
  });
  contextHolder.context.displayName = 'NavigationContext';
}
window.__NAV_CONTEXT_HOLDER = contextHolder;

const NavigationContext = contextHolder.context;

// Safely access scroll utilities
const safeCall = (fn, fallback = () => {}) => {
  return (...args) => {
    try {
      return fn(...args);
    } catch (e) {
      console.warn(`Error calling ${fn.name}:`, e);
      return fallback(...args);
    }
  };
};

export const NavigationProvider = ({ children }) => {
  const [activeSection, setActiveSection] = useState('hero');
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const scrollSpyRef = useRef(null);

  const handleSectionChange = (sectionId) => {
    safeCall(ScrollUtils.scrollToSection)(sectionId);
    setActiveSection(sectionId);
  };

  const toggleMobileNav = () => {
    setIsMobileNavOpen(!isMobileNavOpen);
  };

  // Set up scroll spy to track active section
  useEffect(() => {
    let isMounted = true;

    // Small delay to ensure scroll utils are fully loaded
    const timeoutId = setTimeout(() => {
      if (!isMounted) return;

      const sections = ['hero', 'menu', 'about', 'location'];
      try {
        scrollSpyRef.current = ScrollUtils.createScrollSpy(
          sections,
          setActiveSection,
        );
        scrollSpyRef.current.setup();
      } catch (e) {
        console.warn('Error setting up scroll spy:', e);
      }
    }, 200);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      if (scrollSpyRef.current) {
        try {
          scrollSpyRef.current.cleanup();
        } catch (e) {
          console.warn('Error cleaning up scroll spy:', e);
        }
      }
    };
  }, []);

  const contextValue = {
    activeSection,
    isMobileNavOpen,
    handleSectionChange,
    toggleMobileNav,
    closeMobileNav: () => setIsMobileNavOpen(false),
  };

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  return context;
};
